import React, { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { isoDate } from '../lib/time.js';
import { normalizeUrl, isHubspotUrl } from '../lib/url.js';
import { Modal, TierSelect } from './ui.jsx';
import { Select } from './controls/Select.jsx';
import { DatePicker } from './controls/DatePicker.jsx';
import {
  countryOptions,
  loadHolidayCountries,
} from '../lib/holidayCountries.js';

export function UserModal({ data, user, onClose, onCreated, onSaved }) {
  const isEdit = Boolean(user);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    jobTitle: user?.jobTitle || '',
    role: user?.role || 'user',
    teamId: user?.teamId || data.teams[0]?.id,
    active: user?.active ?? true,
    country: user?.country || 'CZ',
    timesheetExempt: user?.timesheetExempt ?? false,
  });
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    // The list only names the options; saving works without it.
    loadHolidayCountries()
      .then(setCountries)
      .catch(() => {});
  }, []);

  async function submit(event) {
    event.preventDefault();
    if (isEdit) {
      await api(`/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      onSaved();
      return;
    }
    const result = await api('/users', {
      method: 'POST',
      body: JSON.stringify(form),
    });
    onCreated(result.initPassword);
  }

  return (
    <Modal title={isEdit ? 'Edit member' : 'Add member'} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          Name
          <input
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={form.email}
            onChange={(event) =>
              setForm({ ...form, email: event.target.value })
            }
            required
          />
        </label>
        <label>
          Job title
          <input
            value={form.jobTitle}
            onChange={(event) =>
              setForm({ ...form, jobTitle: event.target.value })
            }
          />
        </label>
        <div className="two">
          <label>
            Role
            <Select
              ariaLabel="Role"
              value={form.role === 'project_manager' ? 'user' : form.role}
              onChange={(role) => setForm({ ...form, role })}
              options={[
                { value: 'user', label: 'User' },
                { value: 'admin', label: 'Admin' },
              ]}
            />
          </label>
          <label>
            Team
            <Select
              ariaLabel="Team"
              value={form.teamId}
              onChange={(teamId) => setForm({ ...form, teamId })}
              options={data.teams.map((team) => ({
                value: team.id,
                label: team.name,
              }))}
            />
          </label>
        </div>
        <label>
          Public holidays
          <Select
            ariaLabel="Public holidays"
            value={form.country}
            onChange={(country) => setForm({ ...form, country })}
            options={countryOptions(countries, form.country)}
          />
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.timesheetExempt}
            onChange={(event) =>
              setForm({ ...form, timesheetExempt: event.target.checked })
            }
          />{' '}
          Exempt from timesheet reminders
        </label>
        {isEdit && (
          <label className="check">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) =>
                setForm({ ...form, active: event.target.checked })
              }
            />{' '}
            Active user
          </label>
        )}
        {!isEdit && (
          <p className="hint">
            The backend will generate an initial password and mark the account
            for password change.
          </p>
        )}
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="primary">
            {isEdit ? 'Save member' : 'Create user'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ClientModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '' });

  async function submit(event) {
    event.preventDefault();
    await api('/clients', { method: 'POST', body: JSON.stringify(form) });
    onCreated();
  }

  return (
    <Modal title="New client" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <label>
          Name
          <input
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </label>
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="primary">Create client</button>
        </div>
      </form>
    </Modal>
  );
}

// Teams are managed in one place: rename a team and set its 1..n team
// managers — the people who get read-only visibility into that team in the
// Team view. A save keeps the modal open (several teams are usually edited in
// one pass) and reloads /bootstrap so the rows below stay truthful.
export function TeamModal({ data, reload, onClose }) {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', managerIds: [] });
  const [newTeam, setNewTeam] = useState({ name: '', managerIds: [] });
  const [busy, setBusy] = useState(false);
  // A failure belongs next to the form that caused it: `where` says whether to
  // render it in the row being edited or under the new-team form.
  const [error, setError] = useState(null);

  function managersOf(teamId) {
    return data.users.filter((user) =>
      (user.managedTeamIds || []).includes(teamId),
    );
  }

  function startEdit(team) {
    setError(null);
    setEditingId(team.id);
    setForm({
      name: team.name,
      managerIds: managersOf(team.id).map((user) => user.id),
    });
  }

  async function submit(request, where) {
    setBusy(true);
    setError(null);
    try {
      await request();
      await reload();
      return true;
    } catch (err) {
      setError({ where, message: err.message });
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function saveTeam(teamId) {
    const saved = await submit(
      () =>
        api(`/teams/${teamId}`, { method: 'PUT', body: JSON.stringify(form) }),
      'edit',
    );
    if (saved) setEditingId(null);
  }

  async function addTeam(event) {
    event.preventDefault();
    const created = await submit(
      () => api('/teams', { method: 'POST', body: JSON.stringify(newTeam) }),
      'add',
    );
    if (created) setNewTeam({ name: '', managerIds: [] });
  }

  return (
    <Modal title="Manage teams" onClose={onClose}>
      <div className="form-grid">
        <div className="team-list">
          {data.teams.map((team) =>
            editingId === team.id ? (
              <div key={team.id} className="team-row team-row-editing">
                <label>
                  Team name
                  <input
                    value={form.name}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                  />
                </label>
                <div>
                  <span className="modal-label">Team managers</span>
                  <UserCheckList
                    users={data.users}
                    selected={form.managerIds}
                    onChange={(managerIds) => setForm({ ...form, managerIds })}
                  />
                  <p className="hint">
                    A team manager sees the Team tab with the people of this
                    team and their logged time. Editing members stays with
                    admins.
                  </p>
                </div>
                {error?.where === 'edit' && (
                  <div className="error">{error.message}</div>
                )}
                <div className="team-row-actions">
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={busy || form.name.trim().length < 2}
                    onClick={() => saveTeam(team.id)}
                  >
                    Save team
                  </button>
                </div>
              </div>
            ) : (
              <div key={team.id} className="team-row">
                <div className="team-row-main">
                  <strong>{team.name}</strong>
                  <span>{managerSummary(managersOf(team.id))}</span>
                </div>
                <div className="team-row-actions">
                  <span>{memberCount(data, team.id)}</span>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => startEdit(team)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ),
          )}
        </div>
        <form className="form-grid" onSubmit={addTeam}>
          <label>
            New team name
            <input
              value={newTeam.name}
              onChange={(event) =>
                setNewTeam({ ...newTeam, name: event.target.value })
              }
              required
            />
          </label>
          <div>
            <span className="modal-label">Team managers</span>
            <UserCheckList
              users={data.users}
              selected={newTeam.managerIds}
              onChange={(managerIds) => setNewTeam({ ...newTeam, managerIds })}
            />
          </div>
          {error?.where === 'add' && (
            <div className="error">{error.message}</div>
          )}
          <div className="modal-actions">
            <span />
            <button type="button" className="secondary" onClick={onClose}>
              Close
            </button>
            <button className="primary" disabled={busy}>
              Add team
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

function memberCount(data, teamId) {
  const count = data.users.filter((user) => user.teamId === teamId).length;
  return `${count} ${count === 1 ? 'member' : 'members'}`;
}

function managerSummary(managers) {
  if (!managers.length) return 'No team manager';
  return `Managers: ${managers.map((manager) => manager.name).join(', ')}`;
}

export function ProjectModal({ data, onClose, onCreated }) {
  const [form, setForm] = useState({
    clientId: data.clients[0]?.id,
    code: '',
    name: '',
    groupName: '',
    tier: null,
    hubspotUrl: '',
    billable: true,
    supportProject: false,
    color: 'teal',
    budgetMD: 40,
    startDate: isoDate(new Date()),
    endDate: '',
    managerIds: [],
  });
  const [error, setError] = useState('');

  async function submit(event) {
    event.preventDefault();
    const hubspotUrl = normalizeUrl(form.hubspotUrl);
    if (hubspotUrl && !isHubspotUrl(hubspotUrl)) {
      setError('HubSpot must be a hubspot.com link (or left empty).');
      return;
    }
    setError('');
    try {
      await api('/projects', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          hubspotUrl,
          budgetMD: Number(form.budgetMD),
          endDate: form.endDate || null,
        }),
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <Modal title="New project" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <div className="two">
          <label>
            Code
            <input
              value={form.code}
              onChange={(event) =>
                setForm({ ...form, code: event.target.value })
              }
              required
            />
          </label>
          <label>
            Client
            <Select
              ariaLabel="Client"
              value={form.clientId}
              onChange={(clientId) => setForm({ ...form, clientId })}
              options={data.clients.map((client) => ({
                value: client.id,
                label: client.name,
              }))}
            />
          </label>
        </div>
        <label>
          Name
          <input
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </label>
        <div className="two">
          <label>
            Group
            <input
              value={form.groupName}
              onChange={(event) =>
                setForm({ ...form, groupName: event.target.value })
              }
              maxLength={60}
            />
          </label>
          <label>
            Tier
            <TierSelect
              value={form.tier}
              onChange={(tier) => setForm({ ...form, tier })}
            />
          </label>
        </div>
        <label>
          HubSpot
          <input
            type="text"
            inputMode="url"
            placeholder="https://app.hubspot.com/..."
            value={form.hubspotUrl}
            onChange={(event) =>
              setForm({ ...form, hubspotUrl: event.target.value })
            }
          />
        </label>
        <div className="three">
          <label>
            Budget MD
            <input
              type="number"
              value={form.budgetMD}
              onChange={(event) =>
                setForm({ ...form, budgetMD: event.target.value })
              }
            />
          </label>
          <label>
            Start
            <DatePicker
              ariaLabel="Start"
              value={form.startDate}
              onChange={(startDate) => setForm({ ...form, startDate })}
            />
          </label>
          <label>
            End
            <DatePicker
              ariaLabel="End"
              value={form.endDate}
              onChange={(endDate) => setForm({ ...form, endDate })}
            />
          </label>
        </div>
        <div>
          <span className="modal-label">Managers</span>
          <UserCheckList
            users={data.users}
            selected={form.managerIds}
            onChange={(managerIds) => setForm({ ...form, managerIds })}
          />
        </div>
        <div className="two">
          <label className="check">
            <input
              type="checkbox"
              checked={form.billable}
              onChange={(event) =>
                setForm({ ...form, billable: event.target.checked })
              }
            />{' '}
            Client work project
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={form.supportProject}
              onChange={(event) =>
                setForm({ ...form, supportProject: event.target.checked })
              }
            />{' '}
            Support project
          </label>
        </div>
        {error && <div className="error">{error}</div>}
        <div className="modal-actions">
          <span />
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="primary">Create project</button>
        </div>
      </form>
    </Modal>
  );
}

export function UserCheckList({ users, selected, onChange }) {
  function toggle(id) {
    onChange(
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );
  }

  // Somebody already assigned stays on the list after being deactivated, or a
  // full-replacement save would silently keep an assignment whose checkbox is
  // gone — and hand their access back the day the account is reactivated.
  const listed = users.filter(
    (user) => user.active || selected.includes(user.id),
  );

  return (
    <div className="inline-check-list">
      {listed.map((user) => (
        <label key={user.id} className="check">
          <input
            type="checkbox"
            checked={selected.includes(user.id)}
            onChange={() => toggle(user.id)}
          />
          <span className="truncate">
            {user.name}
            {user.active ? '' : ' (inactive)'}
          </span>
        </label>
      ))}
    </div>
  );
}
